import React, { useEffect } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { Transition } from '@headlessui/react';
import { XMarkIcon as CloseIcon } from '@heroicons/react/24/solid';
import { LineItem } from 'shared/types/cart/LineItem';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { useFormat } from 'helpers/hooks/useFormat';
import UVPPrice from 'helpers/utils/uvpPrice';
import { isPriceGreaterThanOrEqual } from 'helpers/utils/uvpPriceComparison';
import { useLocale } from 'frontastic/hooks/useLocale';
import Image from 'frontastic/lib/image';

interface ModalProps {
  show: boolean;
  onClose: () => void;
  items?: LineItem[];
}

const ProductModal: React.FC<ModalProps> = ({ show, onClose, items }) => {
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });
  const { locale } = useLocale();

  // Check if show is false or items is null/empty
  if (!show || !items || items?.length === 0) return null;

  return (
    <Transition
      show={show}
      className="fixed bottom-0 right-0 z-[9999] w-full overflow-hidden rounded-[20px_20px_0_0] bg-white shadow md:bottom-[unset] md:left-1/2 md:top-1/2 md:w-[90%] md:max-w-[650px] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-lg"
      enter="transition md:transition-opacity duration-75"
      enterFrom="opacity-0 translate-y-full"
      enterTo="opacity-100 translate-y-0"
      leave="transition md:transition-opacity duration-150"
      leaveFrom="opacity-100 translate-y-0"
      leaveTo="opacity-0 translate-y-full"
    >
      <div onMouseUp={(e) => e.stopPropagation()} className="flex h-full flex-col">
        <div className="bg-white p-16 sm:px-8 sm:py-12 md:px-48 md:py-24">
          <h4 className="text-18 leading-[27px] lg:text-22 lg:leading-[33px]">
            {formatProductMessage({ id: 'cart.added', defaultMessage: 'Added to cart' })}
          </h4>
          <CloseIcon
            className="absolute right-[18px] top-[16px] h-28 w-28 cursor-pointer fill-secondary-black stroke-0 sm:right-[12px] sm:top-[12px] md:top-[16px]"
            onClick={onClose}
          />
        </div>

        <div className="h-[200px] grow overflow-y-scroll p-16 sm:px-8 sm:py-12 md:px-48 md:py-24">
          {items?.map((item: LineItem, i: number) => {
            const lineItemType = item.lineItemType;
            const textOverlay = lineItemType === 'bonus' ? 'GRATIS' : lineItemType === 'upsell' ? 'KOMBI' : '';
            const currencyCode = item?.price?.currencyCode || item?.servicePrice?.currencyCode;
            const fractionDigits = item?.price?.fractionDigits || item?.servicePrice?.fractionDigits;
            const uvpPriceValue = item?.variant?.attributes?.uvp_price;
            let uvpPrice = null;
            if (typeof uvpPriceValue === 'string' && uvpPriceValue.startsWith('{')) {
              try {
                uvpPrice = JSON.parse(uvpPriceValue);
              } catch (error) {
                console.error('Failed to parse UVP price JSON', error);
                uvpPrice = null;
              }
            } else {
              uvpPrice = uvpPriceValue || null;
            }
            const showUVP = isPriceGreaterThanOrEqual(uvpPrice, item?.price);

            return (
              <div className="border-t border-gray-200 py-16 sm:px-4 sm:py-8 md:ml-20 lg:grid-cols-2" key={i}>
                <div className="relative mt-12 sm:mt-8 md:mt-15 lg:mt-15">
                  <div className="flex items-center gap-24">
                    {textOverlay && (
                      <div
                        className={`absolute left-[-50px] top-1/2 h-[21px] w-[114px] -translate-y-1/2 rotate-[-90deg] items-center rounded-t-md text-center text-xs font-bold uppercase text-white ${
                          lineItemType === 'bonus' ? 'bg-[#c7181f]' : 'bg-[#3eb100]'
                        }`}
                      >
                        {textOverlay}
                      </div>
                    )}
                    <div className="shrink-0">
                      <Image
                        width={135}
                        height={150}
                        src={item?.variant?.images?.[0]}
                        style={{ objectFit: 'contain' }}
                        alt=""
                      />
                    </div>
                    <div className="flex grow items-center justify-between gap-4 overflow-hidden">
                      <div className="flex w-[60%] flex-col gap-1 overflow-hidden">
                        <p
                          className="text-balance text-wrap max-w-[80%] truncate text-12 font-semibold uppercase leading-loose"
                          title={item?.name}
                        >
                          {item?.name}
                        </p>
                        <p className="truncate text-10">
                          {typeof item?.variant?.attributes?.product_main_attribute === 'string'
                            ? item?.variant?.attributes?.product_main_attribute
                            : item?.variant?.attributes?.product_main_attribute?.[locale]?.value}
                        </p>
                        <p className="truncate text-10">
                          {formatCartMessage({ id: 'article.number', defaultMessage: 'Article number' })}:{' '}
                          {item?.variant?.sku}
                        </p>
                      </div>

                      <div className="flex w-[10%] items-center justify-center">
                        <span className="block border p-2 text-14 text-secondary-black">1</span>
                      </div>

                      <div className="flex w-[30%] flex-col items-center gap-1 text-center">
                        {showUVP && uvpPrice && (
                          <span className="text-12 text-gray-500 line-through">
                            <UVPPrice variant={item?.variant} />
                          </span>
                        )}
                        <span className="text-14 font-medium text-accent-red">
                          {CurrencyHelpers.formatForCurrency(
                            item?.discountedPrice || (item?.price ?? 0),
                            locale,
                            currencyCode,
                            fractionDigits,
                          )}
                        </span>
                        {lineItemType === 'upsell' && (
                          <div className="flex flex-col items-center text-10 font-semibold text-black md:text-12">
                            <span className="truncate">
                              {formatCartMessage({
                                id: 'Last lowest price',
                                defaultMessage: 'Letzter niedrigster Preis',
                              })}
                            </span>
                            <span>
                              {CurrencyHelpers.formatForCurrency(
                                (item?.price || item?.discountedPrice?.centAmount || 0) as number,
                                locale,
                                currencyCode,
                                fractionDigits,
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {item?.servicePrice && (
                  <div className="mt-16 bg-gray-100 px-12 py-4">
                    <div className="mr-6 flex items-center justify-end text-12" key={item?.lineItemId}>
                      <p className="mr-40 text-black">
                        {formatCartMessage({ id: 'service.price', defaultMessage: 'Service & Price' })} :
                      </p>
                      <p>
                        <span className="">{CurrencyHelpers.formatForCurrency(item?.servicePrice ?? 0, locale)}</span>
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="sticky bottom-0 mt-18 flex w-full flex-col gap-16 bg-white p-16 sm:mt-12 sm:flex-col sm:gap-8 md:mt-36 md:flex-row-reverse">
          <Link href="/cart" onClick={onClose} className="sm:w-full md:flex-1">
            <button className="w-full rounded-sm bg-primary-black p-12 text-14 font-medium text-white">
              {formatCartMessage({ id: 'cart.go', defaultMessage: 'Go to cart' })}
            </button>
          </Link>
          <div onClick={onClose} className="sm:w-full md:flex-1">
            <button className="w-full rounded-sm border border-primary-black p-12 text-14 font-medium text-primary-black transition hover:border-secondary-black hover:text-secondary-black">
              {formatCartMessage({ id: 'continue.shopping', defaultMessage: 'Continue shopping' })}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  );
};

export default ProductModal;
